import {Deferred, schedule} from "../../../common/utils/promises";
import {LoadAfterConfirmationEvents} from "../../../page/elements/loadAfterConfirmation";
import {IntersectionObserverFactory, onceIntersected, ResizeObserverFactory} from "../../../common/observation";
import {resolve} from "../../../container";
import {YoutubeIframeApiLoader, YoutubeService, YoutubeVideoProgress} from "./youtube";
import {UnLitElement} from "../../../common/elements";
import {customElement, property} from "lit/decorators.js";
import {type VideoTracking, VideoTrackingFactory} from "../../../tracking/videoTracking";
import {EventBus} from "../../../common/eventBus";
import {DIALOG_CLOSED_EVENT, DIALOG_OPENED_EVENT} from "../../../page/elements/dialog/dialog";
import {EopErrorHandler} from "../../../common/errorHandler";

const YOUTUBE_NOCOOKIE_HOST = "https://www.youtube-nocookie.com";

@customElement("eop-youtube-player")
export class EopYoutubePlayer extends UnLitElement {

    @property({attribute: "aspect-ratio"})
    public aspectRatio: string;

    private player: YT.Player;
    private intersectionObserver: IntersectionObserver;
    private intersectionDeferred: Deferred<void>;
    private playerReadyDeferred: Deferred<void>;
    private confirmationAbortController: AbortController;

    private videoTracking: VideoTracking;
    private readonly handlePausing = (): void => {
        void this.pauseVideo();
    };

    public constructor(
        private youtubeIframeApiLoader: YoutubeIframeApiLoader = resolve(YoutubeIframeApiLoader),
        private youtubeService: YoutubeService = resolve(YoutubeService),
        private loadAfterConfirmationEvents: LoadAfterConfirmationEvents = resolve(LoadAfterConfirmationEvents),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private videoTrackingFactory: VideoTrackingFactory = resolve(VideoTrackingFactory),
        private eventBus: EventBus = resolve(EventBus),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.intersectionDeferred = new Deferred<void>();
        this.playerReadyDeferred = new Deferred<void>();

        this.eventBus.on(DIALOG_OPENED_EVENT, this.handlePausing);
        this.eventBus.on(DIALOG_CLOSED_EVENT, this.handlePausing);

        this.intersectionObserver = this.intersectionObserverFactory.create(onceIntersected(
                () => this.intersectionDeferred.resolve()),
            {threshold: 0, rootMargin: "100% 0% 100% 0%"}
        );
        this.intersectionObserver.observe(this);

        this.confirmationAbortController = this.loadAfterConfirmationEvents.onConfirmation("youtube", () => {
            return schedule(this.handleConfirmation()).as("iframe-api-loaded");
        });
    }

    private async handleConfirmation(): Promise<void> {
        try {
            await this.intersectionDeferred.promise;
            await this.youtubeIframeApiLoader.run();
            this.player = new YT.Player(this.querySelector(".video-placeholder")!, this.playerOptions());
            const videoInfo = await this.youtubeService.getVideoInfo(this.getAttribute("video-id") ?? "");
            this.videoTracking = this.videoTrackingFactory.create(videoInfo.title ?? "", new YoutubeVideoProgress(this.player));

            this.resizeObserverFactory.create(() => {
                const dimensions = this.calculateDimensions();
                this.player.setSize(dimensions.width, dimensions.height);
            }).observe(document.body);
        } catch (e) {
            this.errorHandler.report(e);
        }
    }

    private playerOptions(): YT.PlayerOptions {
        const dimensions = this.calculateDimensions();
        return {
            host: YOUTUBE_NOCOOKIE_HOST,
            videoId: this.getAttribute("video-id") ?? "",
            events: {
                onStateChange: (ev) => this.handleStateChange(ev),
                onReady: () => this.playerReadyDeferred.resolve()
            },
            width: dimensions.width,
            height: dimensions.height,
            playerVars: {
                iv_load_policy: 3,
                rel: 0,
                origin: this.getAttribute("origin-stage") ?? undefined,
                enablejsapi: 1
            }
        };
    }

    public disconnectedCallback(): void {
        this.intersectionDeferred.rejectExpected();
        this.playerReadyDeferred.rejectExpected();

        this.eventBus.remove(DIALOG_OPENED_EVENT, this.handlePausing);
        this.eventBus.remove(DIALOG_CLOSED_EVENT, this.handlePausing);
        if (this.videoTracking) {
            this.videoTracking.disconnect();
        }
        this.intersectionObserver.disconnect();
        this.confirmationAbortController.abort();
        super.disconnectedCallback();
    }

    public async playVideo(): Promise<void> {
        try {
            await this.playerReadyDeferred.promise;
            this.player.playVideo();
        } catch (e) {
            this.errorHandler.report(e);
        }
    }

    public async pauseVideo(): Promise<void> {
        try {
            await this.playerReadyDeferred.promise;
            this.player.pauseVideo();
        } catch (e) {
            this.errorHandler.report(e);
        }
    }

    private handleStateChange(event: YT.OnStateChangeEvent): void {
        if (!this.videoTracking) {
            return;
        }
        const playerState = event.data;
        if (playerState === YT.PlayerState.PLAYING) {
            this.videoTracking.playing();
        }
        if (playerState === YT.PlayerState.PAUSED) {
            this.videoTracking.pause();
        }
        if (playerState === YT.PlayerState.ENDED) {
            this.videoTracking.ended();
        }
    };

    private calculateDimensions(): { height: number, width: number } {
        let height;
        let width = this.findNearestParentWithWidth(this);
        if (this.aspectRatio === "vertical") {
            const viewportHeight = window.innerHeight;
            const ratio = viewportHeight / width;
            if (ratio > 1.77) {
                height = width * 1.77;
            } else {
                height = viewportHeight;
                width = viewportHeight / 1.77;
            }
            return {height: height, width: width};
        } else {
            height = width / 1.77;
            return {height: height, width: width};
        }
    }

    private findNearestParentWithWidth(element: HTMLElement): number {
        return element.parentElement?.classList.contains("youtube-container") ? element.parentElement.offsetWidth : this.findNearestParentWithWidth(element.parentElement!);
    }
}